import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, FilePlus2, History, Search, BarChart3,
  Award, ListChecks, Users, LogOut, ClipboardCheck,
  BookOpen, FileText, FolderOpen, UserCheck, Hand, Download, CheckSquare, Eye,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

const sectionsByRole = {
  admin: [
    { heading: null, links: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/admin/users", label: "Users", icon: Users },
      { to: "/admin/charts", label: "All Charts", icon: ListChecks },
      { to: "/admin/assign", label: "Assign Charts", icon: UserCheck },
      { to: "/reports", label: "Quality Reports", icon: Download },
    ]},
  ],
  manager: [
    { heading: null, links: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/admin/charts", label: "All Charts", icon: ListChecks },
      { to: "/admin/assign", label: "Assign Charts", icon: UserCheck },
      { to: "/admin/users", label: "Team", icon: Users },
      { to: "/reports", label: "Quality Reports", icon: Download },
    ]},
  ],
  team_lead: [
    { heading: null, links: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/admin/charts", label: "All Charts", icon: ListChecks },
      { to: "/admin/assign", label: "Assign Charts", icon: UserCheck },
      { to: "/admin/users", label: "Team", icon: Users },
      { to: "/reports", label: "Quality Reports", icon: Download },
    ]},
  ],
  hr: [
    { heading: null, links: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/admin/users", label: "Team", icon: Users },
    ]},
  ],
  ceo: [
    { heading: null, links: [
      { to: "/dashboard", label: "Dashboard", icon: Eye },
    ]},
  ],
  coder: [
    { heading: "Coder", links: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/coder/submit", label: "Submit Chart", icon: FilePlus2 },
      { to: "/coder/history", label: "History", icon: History },
      { to: "/coder/search", label: "Search Charts", icon: Search },
      { to: "/coder/productivity", label: "Productivity", icon: BarChart3 },
      { to: "/coder/quality", label: "My Quality", icon: Award },
      { to: "/coder/acknowledgements", label: "Acknowledgements", icon: CheckSquare },
    ]},
    { heading: "OASIS", links: [
      { to: "/oasis/items", label: "OASIS Items", icon: BookOpen },
      { to: "/oasis/full", label: "Full OASIS", icon: FileText },
      { to: "/oasis/saved", label: "Saved OASIS", icon: FolderOpen },
    ]},
  ],
  qa: [
    { heading: null, links: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/qa/pool", label: "Pickup Pool", icon: Hand },
      { to: "/qa/assigned", label: "Assigned Charts", icon: ClipboardCheck },
      { to: "/reports", label: "Quality Reports", icon: Download },
    ]},
  ],
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const sections = sectionsByRole[user?.role] ?? [];

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 hidden md:flex flex-col">
        <div className="px-6 py-6 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <img src="/solemnity-logo.jpg" alt="Solemnity logo" className="w-10 h-10 rounded-lg object-contain bg-white border border-slate-100" />
            <div>
              <div className="font-heading font-semibold text-slate-900 leading-tight">Solemnity</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Delivers Quality</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {sections.map((section, idx) => (
            <div key={idx} className={idx > 0 ? "mt-5" : ""}>
              {section.heading && (
                <div
                  data-testid={`nav-section-${section.heading.toLowerCase()}`}
                  className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400"
                >
                  {section.heading}
                </div>
              )}
              <div className="space-y-1">
                {section.links.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-sky-50 text-sky-800"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }`
                    }
                  >
                    <Icon size={16} strokeWidth={2} />
                    {label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-slate-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-slate-200 grid place-items-center text-sm font-semibold text-slate-700">
              {user?.name?.[0] ?? "?"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate" data-testid="sidebar-user-name">
                {user?.name}
              </div>
              <div className="text-xs text-slate-500 capitalize">{(user?.role || "").replace("_", " ")}</div>
            </div>
            <NotificationBell />
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 border border-slate-200 rounded-lg py-2 hover:bg-slate-50"
          >
            <LogOut size={14} /> Log out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
